// The flow at the top of the Reviews lens: what the scope's reviews spotted in
// the range, what the pipeline kept of it, how much of what it kept Triage
// has checked, and what of that needs a person, by the kept standards.
//
// Four columns, each a step that carries some findings on and lets the rest
// go as one dropped node, so a reader follows one path to what needs them:
//   written -> kept | filtered (withheld, context only, unsupported)
//   kept    -> triaged | waiting (not triaged, not loaded)
//   triaged -> met | cleared (standard not met, set aside) | not asked yet
//   met     -> one node per standard that flagged them
//
// What needs a person ends in the standards that flagged it, each finding
// under the first standard that did, so the column adds up; the met node keeps
// every standard's own count for its card. What the pipeline filtered out is
// one node stacked by why.
//
// The first split is the report headers' own arithmetic (every finding a
// review wrote is kept, withheld, context-only or unsupported); the rest is
// the lens's findings pool read against Triage's verdicts, as the Triage tab
// reads them. A merged node keeps its parts, for the card that explains it.
//
// The columns are a funnel, and a funnel's last steps are a few percent of
// its first: drawn to one scale, what needs a person would be a hairline.
// So each column fills the height with the node it splits, and the strands
// widen from that node into it. Every node carries its count and its share of
// the node it came from, so the widening never stands in for a number.
import { ASKABLE_VERDICTS } from "./triageLite.js";

const num = (v) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : 0; };
const sessionsOf = (findings) => new Set(findings.map((f) => f?.session).filter(Boolean)).size;

// What the standards made of a checked finding, in the Triage tab's sections:
// only a finding found true or partly true is put to a standard.
export function flowOutcome(verdict) {
    if (!ASKABLE_VERDICTS.includes(verdict?.verdict)) return "set_aside";
    if (verdict.escalates) return "met";
    return verdict.standardAsked ? "not_met" : "not_asked";
}

export const FLOW_COLUMNS = ["written", "kept", "triage", "standard"];

// -> { columns: [{ id, nodes }], nodes: Map, links, read, unloaded, triaged, empty }.
// `judged`: the report headers' sums for the scope and range, or null while
// they are unread (the flow then starts at the kept findings). `findings`:
// the pool as the triage map holds it ({ id, session }), or null while it is
// unread or unreadable. `clipped`: the pool stopped short of the range.
// `verdicts`: Triage's ledger as the kept standards answer it.
export function buildReviewFlow({ judged = null, findings = null, clipped = false, verdicts = {} } = {}) {
    const read = Array.isArray(findings) ? findings : null;
    const nodes = new Map();
    const links = [];
    const columns = [];
    // A node with nothing in it is not drawn; a dropped node lists its parts
    // that hold anything.
    const add = (node) => { nodes.set(node.id, node); return node; };
    const link = (parent, child, value) => { if (value > 0) links.push({ parent, child, value }); };
    const partsOf = (pairs) => pairs.filter(([, n]) => n > 0).map(([id, count]) => ({ id, count }));

    const written = judged ? num(judged.written) : 0;
    const keptCount = judged ? num(judged.kept) : (read ? read.length : 0);
    // What the lens has not read of the kept findings, when the pool stopped
    // short: older than the newest it holds, so no verdict.
    const unloaded = read && clipped ? Math.max(0, keptCount - read.length) : 0;

    if (judged) {
        add({ id: "written", col: 0, label: "Spotted", count: written, parent: null, sessions: num(judged.sessions) || null });
        columns.push({ id: "written", nodes: ["written"] });
        const parts = partsOf([["withheld", num(judged.withheld)], ["context_only", num(judged.contextOnly)], ["unsupported", num(judged.unsupported)]]);
        const filtered = parts.reduce((n, p) => n + p.count, 0);
        // The kept node is the next column's parent whatever it holds.
        add({ id: "kept", col: 1, label: "Kept", count: keptCount, parent: "written", kind: "continue" });
        link("written", "kept", keptCount);
        const ids = keptCount > 0 ? ["kept"] : [];
        if (filtered > 0) {
            add({ id: "filtered", col: 1, label: "Filtered out", count: filtered, parent: "written", kind: "drop", parts, stacked: true });
            link("written", "filtered", filtered);
            ids.push("filtered");
        }
        columns.push({ id: "kept", nodes: ids });
    } else {
        add({ id: "kept", col: 0, label: "Kept", count: keptCount, parent: null, kind: "continue" });
        columns.push({ id: "kept", nodes: keptCount > 0 ? ["kept"] : [] });
    }
    const kept = nodes.get("kept");
    if (read) kept.sessions = sessionsOf(read);

    let triagedCount = 0;
    if (read) {
        const triaged = read.filter((f) => verdicts?.[f.id]);
        const untriaged = read.filter((f) => !verdicts?.[f.id]);
        triagedCount = triaged.length;
        const col = kept.col + 1;
        const ids = [];
        if (triaged.length) {
            add({ id: "triaged", col, label: "Triaged", count: triaged.length, parent: "kept", kind: "continue", sessions: sessionsOf(triaged) });
            link("kept", "triaged", triaged.length);
            ids.push("triaged");
        }
        const waiting = untriaged.length + unloaded;
        if (waiting) {
            add({
                id: "waiting", col, label: "Not checked yet", count: waiting, parent: "kept", kind: "drop",
                parts: partsOf([["not_triaged", untriaged.length], ["not_loaded", unloaded]]), sessions: untriaged.length ? sessionsOf(untriaged) : null,
            });
            link("kept", "waiting", waiting);
            ids.push("waiting");
        }
        columns.push({ id: "triage", nodes: ids });

        const outcomes = { met: [], not_met: [], not_asked: [], set_aside: [] };
        for (const f of triaged) outcomes[flowOutcome(verdicts[f.id])].push(f);
        const verdictMix = {};
        for (const f of outcomes.set_aside) {
            const v = verdicts[f.id].verdict;
            verdictMix[v] = (verdictMix[v] || 0) + 1;
        }
        const metBy = {};
        for (const f of outcomes.met) for (const name of verdicts[f.id].metBy || []) metBy[name] = (metBy[name] || 0) + 1;
        const cleared = [...outcomes.not_met, ...outcomes.set_aside];
        const out = [];
        const outcome = (id, label, list, extra = {}) => {
            if (!list.length) return;
            add({ id, col: col + 1, label, count: list.length, parent: "triaged", kind: id === "met" ? "continue" : "drop", sessions: sessionsOf(list), ...extra });
            link("triaged", id, list.length);
            out.push(id);
        };
        outcome("met", "Need you", outcomes.met, Object.keys(metBy).length ? { metBy } : {});
        outcome("cleared", "Cleared", cleared, {
            parts: partsOf([["not_met", outcomes.not_met.length], ["set_aside", outcomes.set_aside.length]]),
            ...(outcomes.set_aside.length ? { verdicts: verdictMix } : {}),
        });
        outcome("not_asked", "Not asked yet", outcomes.not_asked);
        columns.push({ id: "standard", nodes: out });

        const flaggedBy = new Map();
        for (const f of outcomes.met) {
            const name = verdicts[f.id].metBy?.[0];
            if (!name) continue;
            if (!flaggedBy.has(name)) flaggedBy.set(name, []);
            flaggedBy.get(name).push(f);
        }
        if (flaggedBy.size) {
            const flagIds = [];
            for (const [name, list] of [...flaggedBy].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))) {
                const id = `flag:${name}`;
                add({ id, col: col + 2, label: name.replace(/ v\d+$/, ""), count: list.length, parent: "met", kind: "continue", flag: true, sessions: sessionsOf(list) });
                link("met", id, list.length);
                flagIds.push(id);
            }
            columns.push({ id: "flagged", nodes: flagIds });
        }
    } else {
        // Unread, the columns still stand where they will be, for what is
        // said in their place.
        columns.push({ id: "triage", nodes: [] }, { id: "standard", nodes: [] });
    }

    for (const node of nodes.values()) {
        const parent = node.parent ? nodes.get(node.parent) : null;
        node.share = parent && parent.count > 0 ? node.count / parent.count : null;
    }
    // Shares are of the node a split comes from, so a column adds up; where
    // the lens read only part of the kept findings, what Triage checked is
    // also a share of what it read.
    const triagedNode = nodes.get("triaged");
    if (triagedNode && unloaded && read?.length) triagedNode.readShare = triagedNode.count / read.length;

    return {
        columns,
        nodes,
        links,
        read: read ? read.length : null,
        unloaded,
        triaged: triagedCount,
        empty: judged ? written === 0 && keptCount === 0 : keptCount === 0,
    };
}

// -- geometry ------------------------------------------------------------------

// Node heights within a column, proportional to count with a floor so a node
// of one is still a mark, summing to `avail`.
function columnHeights(counts, avail, floor) {
    const total = counts.reduce((n, c) => n + c, 0);
    if (!(total > 0) || !(avail > 0)) return counts.map(() => 0);
    const out = counts.map((c) => (c / total) * avail);
    for (let pass = 0; pass < 3; pass++) {
        const small = out.map((h, i) => counts[i] > 0 && h < floor);
        if (!small.some(Boolean)) break;
        const fixed = small.reduce((n, s) => n + (s ? floor : 0), 0);
        const rest = counts.reduce((n, c, i) => n + (small[i] ? 0 : c), 0);
        for (let i = 0; i < out.length; i++) {
            out[i] = small[i] ? floor : rest > 0 ? (counts[i] / rest) * Math.max(0, avail - fixed) : 0;
        }
    }
    return out;
}

// Labels sit beside their node's middle, pushed apart where neighbours would
// overlap and kept inside the plot.
function relaxLabels(items, { height, gap = 2 }) {
    const sorted = [...items].sort((a, b) => a.want - b.want);
    let y = -Infinity;
    for (const it of sorted) {
        it.top = Math.max(it.want - it.h / 2, y + gap, 0);
        y = it.top + it.h;
    }
    let floor = height;
    for (let i = sorted.length - 1; i >= 0; i--) {
        const it = sorted[i];
        if (it.top + it.h > floor) it.top = Math.max(0, floor - it.h);
        floor = it.top - gap;
    }
    return items;
}

// -> { width, height, columns: [{ id, x }], nodes: Map id -> { x, y, w, h, mid, labelTop, segments? }, strands }.
// A stacked node is cut into one segment per part, each with its own label
// in place of the node's.
// The last column keeps a lane to its right for its labels; the others label
// into the gap they flow across.
export function layoutReviewFlow(model, { width, height, bar = 8, gap = 10, lane = 150, floor = 3, labelHeight = 16 } = {}) {
    const cols = model?.columns || [];
    const out = { width, height, columns: [], nodes: new Map(), strands: [] };
    if (!cols.length || !(width > 0) || !(height > 0)) return out;
    const span = Math.max(0, width - lane - bar);
    const step = cols.length > 1 ? span / (cols.length - 1) : 0;
    cols.forEach((col, ci) => {
        const x = ci * step;
        out.columns.push({ id: col.id, x });
        const counts = col.nodes.map((id) => model.nodes.get(id).count);
        const live = counts.filter((c) => c > 0).length;
        const heights = columnHeights(counts, height - gap * Math.max(0, live - 1), floor);
        let y = 0;
        col.nodes.forEach((id, i) => {
            const h = heights[i];
            out.nodes.set(id, { id, col: ci, x, y, w: bar, h, mid: y + h / 2 });
            if (h > 0) y += h + gap;
        });
        const items = [];
        for (const id of col.nodes) {
            const node = model.nodes.get(id);
            const lay = out.nodes.get(id);
            if (!node.stacked || !node.parts?.length) { items.push({ id, want: lay.mid, h: labelHeight }); continue; }
            let sy = lay.y;
            lay.segments = node.parts.map((p) => {
                const sh = node.count > 0 ? (p.count / node.count) * lay.h : 0;
                const seg = { id: p.id, count: p.count, y: sy, h: sh };
                sy += sh;
                items.push({ id, part: p.id, want: seg.y + sh / 2, h: labelHeight });
                return seg;
            });
        }
        for (const l of relaxLabels(items, { height })) {
            const lay = out.nodes.get(l.id);
            if (l.part) lay.segments.find((s) => s.id === l.part).labelTop = l.top;
            else lay.labelTop = l.top;
        }
    });

    // Each node's strands leave its right edge child by child, and each fills
    // its child; both ends are slices of their own node, so the widening
    // between them is the funnel's.
    const childOrder = new Map();
    for (const col of cols) col.nodes.forEach((id, i) => childOrder.set(id, i));
    const byParent = new Map();
    for (const l of model.links) {
        if (!byParent.has(l.parent)) byParent.set(l.parent, []);
        byParent.get(l.parent).push(l);
    }
    for (const [parentId, list] of byParent) {
        const p = out.nodes.get(parentId);
        if (!p) continue;
        const ordered = [...list].sort((a, b) => childOrder.get(a.child) - childOrder.get(b.child));
        const outTotal = ordered.reduce((n, l) => n + l.value, 0);
        let a = p.y;
        for (const l of ordered) {
            const c = out.nodes.get(l.child);
            if (!c || !(outTotal > 0)) continue;
            const a1 = a + (l.value / outTotal) * p.h;
            const x0 = p.x + p.w;
            const x1 = c.x;
            const xm = (x0 + x1) / 2;
            out.strands.push({
                id: `${l.parent}>${l.child}`, parent: l.parent, child: l.child, value: l.value,
                d: `M ${x0} ${a} C ${xm} ${a}, ${xm} ${c.y}, ${x1} ${c.y} L ${x1} ${c.y + c.h} C ${xm} ${c.y + c.h}, ${xm} ${a1}, ${x0} ${a1} Z`,
            });
            a = a1;
        }
    }
    return out;
}
